import * as luxon from 'luxon';
import queryString from 'query-string';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export interface CalendarParams {
  dateTo?: string;
  dateFrom?: string;
  timeSet?: string;
}

/**
 * Returns the clamped [start, end] range when the requested `start` is beyond
 * the configured app upper bound for the given `timeSet`, otherwise null.
 *
 * Each time set is rounded to its own granularity so the clamped range stays
 * meaningful (e.g. for month view we clamp to the start of the upper bound's
 * month, not to a flat day).
 */
function clampRangeToUpperBound(
  timeSet: string,
  start: Date,
  upperBoundIso: string,
): [Date, Date] | null {
  const upperBound = luxon.DateTime.fromISO(upperBoundIso).startOf('day');
  if (!upperBound.isValid) return null;

  const granularity =
    timeSet === 'week' ? 'week' :
    timeSet === 'month' ? 'month' :
    timeSet === 'year' ? 'year' :
    'day';

  const selectedStart = luxon.DateTime.fromJSDate(start).startOf(granularity);
  const allowedStart =
    granularity === 'day' ? upperBound : upperBound.startOf(granularity);

  if (selectedStart <= allowedStart) return null;

  const allowedEnd =
    granularity === 'week'  ? allowedStart.plus({ weeks: 1 }) :
    granularity === 'month' ? allowedStart.plus({ months: 1 }) :
    granularity === 'year'  ? allowedStart.plus({ years: 1 }) :
                              allowedStart.plus({ days: 1 });
  return [allowedStart.toJSDate(), allowedEnd.toJSDate()];
}

export interface UseCalendarParamsOptions {
  /**
   * Current selected date range from calendar state
   */
  selectedDate: [Date, Date];

  /**
   * Calendar Redux action creator for setting hive date
   */
  calendarAction: any;

  /**
   * Whether to automatically process URL parameters on mount
   * @default true
   */
  enabled?: boolean;

  /**
   * Custom URL search params (for testing or custom scenarios)
   * @default window.location.search
   */
  searchParams?: string;
}

export interface UseCalendarParamsReturn {
  /**
   * Manually process calendar parameters from URL
   */
  processCalendarParams: () => void;

  /**
   * Whether calendar parameters are currently being processed
   */
  isProcessing: boolean;

  /**
   * Last processed calendar parameters
   */
  lastProcessedParams: CalendarParams | null;
}

/**
 * Helper hook to clamp the hive calendar selection within sites availability range
 * Call this once at app level in each admin app (admin-kwick, admin-hive, admin-eon)
 */
export function useClampCalendarToSitesRange(options: {
  selectedDate: [Date, Date];
  calendarAction: any;
}): void {
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);
  const selectedTimeSet: string | undefined =
    calendar?.hiveCalendar?.selectedTimeSet;
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';

  const getContext = useCallback((): 'site' | 'community' | 'member' | null => {
    try {
      // Prefer Redux metadata entityType if available
      const entityType = (metadata as any)?.entityType as string | null;
      if (
        entityType === 'site' ||
        entityType === 'community' ||
        entityType === 'member'
      ) {
        return entityType as any;
      }
      // Fallback to pathname parsing
      const seg = (pathname.split('/')[1] || '').toLowerCase();
      if (seg.includes('site') || seg.includes('household')) return 'site';
      if (seg.includes('community')) return 'community';
      if (seg.includes('member')) return 'member';
      return null;
    } catch {
      return null;
    }
  }, [metadata?.entityType, pathname]);

  const getSitesForContext = useCallback(() => {
    const allSites =
      (metadata?.entities as any)?.sites ||
      (metadata?.entities as any)?.site ||
      [];
    const idFromState = (metadata as any)?.selectedEntityId || null;
    const idFromPath = (pathname.split('/')[2] || null) as string | null;
    const id = idFromState || idFromPath;
    const ctx = getContext();
    if (!ctx || !id || !Array.isArray(allSites)) return [];
    switch (ctx) {
      case 'site':
        return allSites.filter((s: any) => s?.siteId === id);
      case 'community':
        return allSites.filter((s: any) => s?.communityId === id);
      case 'member':
        return allSites.filter((s: any) => s?.memberId === id);
      default:
        return [];
    }
  }, [metadata?.entities, metadata?.selectedEntityId, pathname, getContext]);

  const safeDate = (str?: string): Date | null => {
    try {
      return str ? new Date(str) : null;
    } catch {
      return null;
    }
  };

  const getSiteRange = (
    site: any
  ): { start: Date | null; end: Date | null } => {
    const starts: number[] = [];
    const ends: number[] = [];

    const push = (str: string | undefined, arr: number[]) => {
      const d = safeDate(str);
      if (d && !isNaN(d.getTime())) arr.push(d.getTime());
    };

    // Top-level fields
    push(site?.startDate, starts);
    push(site?.joinDate, starts);
    push(site?.endDate, ends);
    push(site?.leaveDate, ends);

    // Changelog: full community-assignment history
    if (site?.changelog?.length > 0) {
      for (const entry of site.changelog) {
        push(entry.startDate, starts);
        push(entry.endDate, ends);
      }
    }

    // SiteType: type-change history
    if (site?.siteType?.length > 0) {
      for (const st of site.siteType) {
        push(st.startDate, starts);
        push(st.endDate, ends);
      }
    }

    return {
      start: starts.length > 0 ? new Date(Math.min(...starts)) : null,
      end: ends.length > 0 ? new Date(Math.max(...ends)) : null,
    };
  };

  const getGlobalRange = useCallback((sites: any[]) => {
    const starts: number[] = [];
    const ends: number[] = [];
    sites.forEach((s) => {
      const { start, end } = getSiteRange(s);
      if (start instanceof Date && !isNaN(start.getTime()))
        starts.push(start.getTime());
      if (end instanceof Date && !isNaN(end.getTime()))
        ends.push(end.getTime());
    });
    if (!starts.length) return null;

    // Cap upper bound at today: future data doesn't exist yet.
    // For active sites (no endDate), use today as maxEnd.
    const now = new Date();
    const defaultMaxEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
    const rawMaxEnd = ends.length > 0 ? Math.max(...ends) : defaultMaxEnd;

    const minStart = new Date(Math.min(...starts));
    const maxEnd = new Date(Math.min(rawMaxEnd, defaultMaxEnd));

    // Guard against an impossible window: when the entity starts after the
    // effective upper bound (e.g. a not-yet-started community whose start_date
    // is in the future), there is no valid range. Returning null makes the
    // clamp effect bail instead of shifting the selection into the future,
    // which would request future data (backend ERR_DATE_001 / 500) and ping-pong
    // against the upper-bound clamp (request storm / max-update-depth crash).
    if (minStart.getTime() > maxEnd.getTime()) return null;

    return {
      minStart,
      maxEnd,
    } as const;
  }, []);

  const getWindowDays = (
    timeSet: string | undefined,
    fallbackDays: number
  ): number => {
    switch (timeSet) {
      case 'day':
        return 1;
      case 'week':
        return 7;
      case 'month':
        return 30;
      case 'year':
        return 365;
      default:
        return Math.max(1, fallbackDays || 1);
    }
  };

  // Clamp selectedDate within global sites range for current context
  useEffect(() => {
    try {
      const [from, to] = options.selectedDate || ([] as any);
      if (!(from instanceof Date) || !(to instanceof Date)) return;

      const sites = getSitesForContext();
      if (!sites?.length) return;
      const range = getGlobalRange(sites);
      if (!range) return;

      const fromMs = from.getTime();
      const toMs = to.getTime();
      if (isNaN(fromMs) || isNaN(toMs)) return;

      const widthDaysCurrent = Math.max(
        1,
        Math.round((toMs - fromMs) / 86400000)
      );
      const wantDays = getWindowDays(selectedTimeSet, widthDaysCurrent);

      let newFrom = new Date(fromMs);
      let newTo = new Date(toMs);

      const minStartMs = range.minStart.getTime();
      const maxEndMs = range.maxEnd.getTime();

      if (toMs < minStartMs) {
        // Selection is completely BEFORE entity range - shift to start
        if (selectedTimeSet === 'year') {
          // Shift to first valid year, starting from Jan 1
          const minYear = range.minStart.getFullYear();
          newFrom = new Date(minYear, 0, 1);
          newTo = new Date(minYear + 1, 0, 1);
        } else if (selectedTimeSet === 'month') {
          // Shift to first valid month, starting from 1st
          newFrom = new Date(
            range.minStart.getFullYear(),
            range.minStart.getMonth(),
            1
          );
          newTo = new Date(
            range.minStart.getFullYear(),
            range.minStart.getMonth() + 1,
            1
          );
        } else {
          // day/week: current behavior
          newFrom = new Date(minStartMs);
          newTo = new Date(minStartMs + wantDays * 86400000);
        }
      } else if (fromMs > maxEndMs) {
        // Selection is completely AFTER entity range - shift to end
        if (selectedTimeSet === 'year') {
          // Shift to last valid year, starting from Jan 1
          const maxYear = range.maxEnd.getFullYear();
          newFrom = new Date(maxYear, 0, 1);
          newTo = new Date(maxYear + 1, 0, 1);
        } else if (selectedTimeSet === 'month') {
          // Shift to last valid month, starting from 1st
          newFrom = new Date(
            range.maxEnd.getFullYear(),
            range.maxEnd.getMonth(),
            1
          );
          newTo = new Date(
            range.maxEnd.getFullYear(),
            range.maxEnd.getMonth() + 1,
            1
          );
        } else {
          // day/week: current behavior
          newTo = new Date(maxEndMs);
          newFrom = new Date(maxEndMs - wantDays * 86400000);
        }
      } else {
        // Selection OVERLAPS with entity range
        if (selectedTimeSet === 'year' || selectedTimeSet === 'month') {
          // For year/month: don't clamp to exact entity boundaries
          // The selected period has data (even partial), keep natural period boundaries
          // No changes needed - newFrom and newTo already have natural period starts
        } else {
          // day/week: clamp to exact entity boundaries
          if (fromMs < minStartMs) newFrom = new Date(minStartMs);
          if (toMs > maxEndMs) newTo = new Date(maxEndMs);
          if (newTo.getTime() - newFrom.getTime() < wantDays * 86400000) {
            const candidateTo = new Date(
              newFrom.getTime() + wantDays * 86400000
            );
            newTo = new Date(Math.min(candidateTo.getTime(), maxEndMs));
          }
        }
      }

      const changed = newFrom.getTime() !== fromMs || newTo.getTime() !== toMs;
      if (changed) {
        dispatch(
          options.calendarAction.setHiveDate({
            timeSet: selectedTimeSet || 'day',
            date: [newFrom, newTo],
            setQueryParams: true,
          })
        );
      }
    } catch {
      // no-op
    }
  }, [
    options.selectedDate,
    metadata?.entities,
    metadata?.selectedEntityId,
    metadata?.entityType,
    pathname,
    selectedTimeSet,
    dispatch,
    getSitesForContext,
    getGlobalRange,
  ]);
}

/**
 * Custom hook to handle calendar URL parameters parsing and processing
 *
 * This hook encapsulates the complex logic for:
 * - Parsing URL parameters for calendar dates
 * - Validating date formats and ranges
 * - Comparing with current selected dates using Luxon
 * - Dispatching Redux actions for calendar updates
 *
 * @param options - Configuration options for calendar parameter processing
 * @returns Calendar parameter processing state and controls
 */
export function useCalendarParams(
  options: UseCalendarParamsOptions
): UseCalendarParamsReturn {
  const {
    selectedDate,
    calendarAction,
    enabled = true,
    searchParams = window.location.search,
  } = options;

  const dispatch = useDispatch();
  const appConfigUpperBound = useSelector(
    (state: any) => state?.appConfig?.calendar?.upperBound || null
  );
  const selectedTimeSet = useSelector(
    (state: any) => state?.calendar?.hiveCalendar?.selectedTimeSet
  );

  // Helper function to check if object has required keys
  const hasKeys = useCallback((obj: any, ...keys: string[]) => {
    return keys.every((key) => key in obj);
  }, []);

  // Main calendar parameter processing logic
  const processCalendarParams = useCallback(() => {
    try {
      const params = queryString.parse(searchParams) as CalendarParams;

      // Check if URL has calendar parameters and required keys exist
      if (
        Object.keys(params).length === 0 ||
        !hasKeys(params, 'dateTo', 'dateFrom', 'timeSet')
      ) {
        return null;
      }

      // Parse dates from URL parameters
      const _startDate = new Date(params.dateFrom as string);
      const _endDate = new Date(params.dateTo as string);

      // Validate parsed dates
      if (`${_startDate} ${_endDate}`.toLowerCase()?.includes('invalid')) {
        console.warn('Invalid date parameters in URL:', params);
        return null;
      }

      const fmt = (d: Date) => luxon.DateTime.fromJSDate(d).toFormat('yyyy-MM-dd');

      let dateRange: [Date, Date] = [_startDate, _endDate];
      let shouldUpdateQueryParams = false;
      if (typeof appConfigUpperBound === 'string') {
        const clamped = clampRangeToUpperBound(
          params.timeSet as string,
          _startDate,
          appConfigUpperBound,
        );
        if (clamped) {
          dateRange = clamped;
          shouldUpdateQueryParams = true;
        }
      }

      // Compare with current selected dates at day granularity.
      // URL params only carry yyyy-MM-dd precision; comparing milliseconds
      // causes an infinite loop when Date objects differ by timezone offset
      // (new Date("2024-10-15") = UTC midnight vs new Date(2024,9,15) = local midnight).
      const startDateMatches = fmt(dateRange[0]) === fmt(selectedDate[0]);
      const endDateMatches = fmt(dateRange[1]) === fmt(selectedDate[1]);

      // Skip if the effective dates are already the same as current selection
      if (startDateMatches && endDateMatches) {
        return null;
      }

      // Dispatch calendar update action
      dispatch(
        calendarAction.setHiveDate({
          timeSet: params.timeSet,
          date: dateRange,
          setQueryParams: shouldUpdateQueryParams,
        })
      );

      return params;
    } catch (error) {
      console.error('Error processing calendar parameters:', error);
      return null;
    }
  }, [searchParams, selectedDate, calendarAction, dispatch, hasKeys, appConfigUpperBound]);

  useEffect(() => {
    if (!enabled || typeof appConfigUpperBound !== 'string' || !selectedTimeSet) {
      return;
    }

    const [from] = selectedDate || ([] as any);
    if (!(from instanceof Date)) return;

    const clamped = clampRangeToUpperBound(
      selectedTimeSet,
      from,
      appConfigUpperBound,
    );
    if (!clamped) return;

    dispatch(
      calendarAction.setHiveDate({
        timeSet: selectedTimeSet,
        date: clamped,
        setQueryParams: true,
      })
    );
  }, [
    enabled,
    selectedTimeSet,
    appConfigUpperBound,
    selectedDate,
    calendarAction,
    dispatch,
  ]);

  // Auto-process calendar parameters on mount if enabled
  useEffect(() => {
    if (enabled) {
      processCalendarParams();
    }
  }, [enabled, processCalendarParams]);

  return {
    processCalendarParams,
    isProcessing: false, // Could be enhanced with loading state if needed
    lastProcessedParams: null, // Could be enhanced to track last processed params
  };
}
